// Trvalé úložiště tabletu pro offline režim (F4 4.5): fronta zápisů a balíček dat na směnu musí přežít
// zavření prohlížeče i restart tabletu, proto IndexedDB (localStorage neunese fotky a podpisy).
// Když IndexedDB není (soukromé okno, staré WebView, jsdom v testech), použije se paměťová náhrada —
// aplikace funguje dál, jen se fronta po zavření prohlížeče ztratí; UI o tom řekne.
const NAZEV_DB = 'ecoser-offline';
const SKLAD = 'hodnoty';

export interface Uloziste {
  ziskej<T>(klic: string): Promise<T | undefined>;
  uloz(klic: string, hodnota: unknown): Promise<void>;
  smaz(klic: string): Promise<void>;
  /** false = data přežijí jen do zavření záložky (IndexedDB není k dispozici). */
  trvale: boolean;
}

function pametove(): Uloziste {
  const data = new Map<string, unknown>();
  return {
    trvale: false,
    async ziskej<T>(klic: string) {
      return data.get(klic) as T | undefined;
    },
    async uloz(klic, hodnota) {
      data.set(klic, hodnota);
    },
    async smaz(klic) {
      data.delete(klic);
    },
  };
}

function otevri(): Promise<IDBDatabase> {
  return new Promise((vyridit, odmitnout) => {
    const pozadavek = indexedDB.open(NAZEV_DB, 1);
    pozadavek.onupgradeneeded = () => {
      const db = pozadavek.result;
      if (!db.objectStoreNames.contains(SKLAD)) db.createObjectStore(SKLAD);
    };
    pozadavek.onsuccess = () => vyridit(pozadavek.result);
    pozadavek.onerror = () => odmitnout(pozadavek.error ?? new Error('IndexedDB se neotevřela'));
  });
}

function transakce<T>(
  rezim: IDBTransactionMode,
  prace: (sklad: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return otevri().then(
    (db) =>
      new Promise<T>((vyridit, odmitnout) => {
        const t = db.transaction(SKLAD, rezim);
        const pozadavek = prace(t.objectStore(SKLAD));
        pozadavek.onsuccess = () => vyridit(pozadavek.result as T);
        pozadavek.onerror = () =>
          odmitnout(pozadavek.error ?? new Error('Zápis do IndexedDB selhal'));
        t.oncomplete = () => db.close();
      }),
  );
}

function indexovane(): Uloziste {
  return {
    trvale: true,
    ziskej<T>(klic: string) {
      return transakce<T | undefined>('readonly', (s) => s.get(klic));
    },
    async uloz(klic, hodnota) {
      await transakce('readwrite', (s) => s.put(hodnota, klic));
    },
    async smaz(klic) {
      await transakce('readwrite', (s) => s.delete(klic));
    },
  };
}

let ulozene: Uloziste | null = null;

/** Úložiště offline režimu (IndexedDB, jinak paměť). */
export function uloziste(): Uloziste {
  if (ulozene) return ulozene;
  const maIndexedDb = typeof indexedDB !== 'undefined' && indexedDB !== null;
  ulozene = maIndexedDb ? indexovane() : pametove();
  return ulozene;
}

/** Jen pro testy: podvrhne úložiště (paměťové) a zapomene předchozí. */
export function nastavUloziste(nove: Uloziste | null): void {
  ulozene = nove;
}

/** Jen pro testy: čerstvé paměťové úložiště. */
export const pametoveUloziste = pametove;
